<script setup lang="ts">
import {
  DataSetDto,
  instanceOfEnumerationDataSetSetupDto,
  instanceOfNumericDataSetSetupDto,
  instanceOfTextDataSetSetupDto,
} from '@/api/generator';
import ChartLegendSeriesIdentifier from '@/components/charts/ChartLegendSeriesIdentifier.vue';
import { getLegendItems } from '@/components/charts/utils/getLegendItems.ts';
import FormLabel from '@/components/FormLabel/FormLabel.vue';
import { EMPTY_FIELD_SIGN } from '@/components/forms/utils.ts';
import { chevronLeft, chevronRight } from '@/components/icons/icons';
import IdentifierLabel from '@/components/IdentifierLabel/IdentifierLabel.vue';
import { DataTableHeader } from '@/components/OverviewTable/types.ts';
import ShowMore from '@/components/ShowMore/ShowMore.vue';
import Tooltip from '@/components/Tooltip/Tooltip.vue';
import { truthy } from '@/utils/helper.ts';
import { toDisplayValue } from '@/utils/numberUtils';
import { getSetupParameterName } from '@/views/tasks/utils.ts';
import {
  DataSetSelectionEmits,
  DataSetSelectionProps,
  isDataSetInternalDataTableHeader,
  RowData,
} from '@/views/TrendsSelectionView/types.ts';
import { useDataSetFiltering } from '@/views/TrendsSelectionView/useDataSetFiltering.ts';
import { t } from 'i18next';
import { has, isNumber, xor } from 'lodash';
import { computed, CSSProperties, nextTick, ref, watch, onMounted } from 'vue';
import { VDataTable } from 'vuetify/components';
import { HScrollDirection, useHDivScroll } from '../useHDivScroll';
import { useDataSetTableModel } from '../useDataSetTableModel';

type DataTableInstance = InstanceType<typeof VDataTable> & { $el: HTMLElement };

defineOptions({
  inheritAttrs: false,
});

const props = defineProps<DataSetSelectionProps>();
const emit = defineEmits<DataSetSelectionEmits>();

const { hasValidDateRange, startDate, endDate } = useDataSetFiltering();

const legendItems = computed(() => getLegendItems(props.dataSets));

const isMaximumDisplayableItemsReached = computed(() =>
  props.showAsLegend || props.maximumDataSetCount === undefined
    ? false
    : props.totalDataSetCount > props.maximumDataSetCount,
);

const filterResultsText = computed(() => {
  if ((startDate.value && !endDate.value) || (!startDate.value && endDate.value)) {
    return t('SetDateFiltersMsg');
  } else if (!hasValidDateRange.value) {
    // User doesnt have a valid date range
    return t('SetValidDateRangeMsg');
  } else if (isMaximumDisplayableItemsReached.value && isNumber(props.dataSets?.length)) {
    return t('MoreThan_maximum_DataSetsAvailable_count_PleaseNarrowDownTheSelectionMsg', {
      maximum: props.maximumDataSetCount,
      count: props.totalDataSetCount,
    });
  } else if (props.dataSets?.length === 0) {
    return t('NoResultsFound');
  } else {
    return undefined;
  }
});

// Generate columns for the v-data-table (transposed layout)
const columns = computed(() => {
  if (isMaximumDisplayableItemsReached.value || !props.dataSets) return [];

  // First column is for headers
  const headerColumn: DataTableHeader<string> = {
    key: 'headerTitle',
    title: 'headerTitle',
    sortable: false,
    align: 'start',
    cellProps: () => ({
      class: 'header-column',
    }),
  };

  // Each dataset is a column
  const datasetColumns: DataTableHeader<string>[] = props.dataSets.map((dataSet, index) => ({
    key: `dataset_${dataSet.id}`,
    title: dataSet.evaluationParameter.name ?? dataSet.id,
    sortable: false,
    align: 'start',
    cellProps: ({ item }: { item: RowData }) => {
      let classList = 'dataset-column dataset-cell';
      if (props.selectedDataSets.includes(dataSet.id)) {
        classList += ' selected';
      }
      if (hoveredColumnIndex.value === index + 1) {
        // +1 because header column is index 0
        classList += ' column-hovered';
      }

      const columnKey = `dataset_${dataSet.id}`;
      const cellStyle: CSSProperties = {};

      // Apply vertical-align: top for support-systems cells
      if (
        item[columnKey]?.type === 'supportSystems' &&
        item[columnKey]?.supportSystems?.length > 0
      ) {
        cellStyle.verticalAlign = 'top';
      }

      return {
        class: classList,
        style: cellStyle,
        onClick: () => toggleSelection(dataSet),
        onMouseenter: () => {
          hoveredColumnIndex.value = index + 1;
        },
        onMouseleave: () => {
          hoveredColumnIndex.value = null;
        },
      };
    },
    dataSet,
  }));

  return [headerColumn, ...datasetColumns];
});

// Build table rows via composable (preserves original behavior)
const { tableData } = useDataSetTableModel(props);

// Scroll div functionality
function toggleSelection(dataSet: DataSetDto) {
  const newSelectedDataSets = xor(props.selectedDataSets, [dataSet.id]);
  emit('update:selectedDataSets', newSelectedDataSets);
}

const tableRef = ref<DataTableInstance | null>(null);

const { startScroll, stopScroll, scrollEl } = useHDivScroll();

function onScrollPress(direction: HScrollDirection) {
  startScroll(direction);
}

const showScrollButtons = ref(false);

function checkScrollButtons() {
  nextTick(() => {
    const scrollDiv = tableRef.value?.$el?.querySelector('.v-table__wrapper') as HTMLElement | null;
    if (scrollDiv) {
      showScrollButtons.value = scrollDiv.scrollWidth > scrollDiv.clientWidth;
      scrollEl.value = scrollDiv;
    }
  });
}

watch(() => props.dataSets, checkScrollButtons);

onMounted(() => {
  checkScrollButtons();
});

// Track hovered column for performance-optimized styling
const hoveredColumnIndex = ref<number | null>(null);
</script>

<template>
  <div
    v-if="dataSets?.length && !isMaximumDisplayableItemsReached"
    class="dataset-selection-wrapper"
  >
    <template v-if="showScrollButtons">
      <Tooltip>
        <template #target="{ props: tooltipProps }">
          <SecondaryVBtn
            v-bind="{ ...tooltipProps }"
            class="scroll-button scroll-button-left"
            size="medium"
            variant="text"
            :icon="chevronLeft"
            @mousedown="onScrollPress('left')"
            @mouseup="stopScroll"
            @mouseleave="stopScroll"
          />
        </template>
        <template #tooltip> {{ $t('UseArrowButtonsOnKeyboard') }} </template>
      </Tooltip>

      <Tooltip>
        <template #target="{ props: tooltipProps }">
          <SecondaryVBtn
            v-bind="{ ...tooltipProps }"
            class="scroll-button scroll-button-right"
            color="primary"
            size="medium"
            variant="text"
            :icon="chevronRight"
            @mousedown="onScrollPress('right')"
            @mouseup="stopScroll"
            @mouseleave="stopScroll"
          />
        </template>
        <template #tooltip> {{ $t('UseArrowButtonsOnKeyboard') }} </template>
      </Tooltip>
    </template>

    <v-data-table
      ref="tableRef"
      v-bind="$attrs"
      density="compact"
      class="trends-table"
      :headers="columns"
      :items="tableData"
      :loading="loading"
      itemsPerPage="-1"
      disableSort
      disablePagination
      hideDefaultHeader
      hideDefaultFooter
      :search="showDifferences ? 'different' : 'identical'"
      :itemClass="(item: RowData) => (item.isSeparator ? 'separator-row' : '')"
      :customFilter="
        (value, query, item) => {
          // Always show separator rows
          if (item?.raw.isSeparator) {
            return true;
          }

          if (item?.raw.isIdenticalOverAllSetupValues === null) {
            return true;
          }

          if (query === 'different') {
            return item?.raw.isIdenticalOverAllSetupValues === false;
          }
          return true;
        }
      "
      @mouseleave="hoveredColumnIndex = null"
    >
      <!-- Header cell  -->
      <template #item.headerTitle="{ item }">
        <div v-if="item.isSeparator" class="separator-header header-cell">
          {{ item.headerTitle }}
        </div>

        <div v-else class="header-cell">
          <template v-if="typeof item.headerTitle === 'string'">
            <IdentifierLabel
              v-if="has(item, 'iuid') && typeof item.iuid === 'string'"
              :label="item.headerTitle"
              :identifier="item.iuid"
            />
            <span v-else class="font-weight-bold">
              {{ item.headerTitle }}
            </span>
          </template>
        </div>
      </template>

      <!-- Dynamic cell rendering based on content type -->
      <template
        v-for="(dataSet, dataSetIndex) in dataSets"
        :key="dataSet?.id"
        #[`item.dataset_${dataSet?.id}`]="{ item, column }"
      >
        <div v-if="item.isSeparator" class="separator-cell"></div>
        <template v-else-if="isDataSetInternalDataTableHeader(column) && column?.key !== null">
          <!-- DataSet Header -->
          <template v-if="item[column.key]?.type === 'datasetHeader'">
            <ChartLegendSeriesIdentifier
              v-if="showAsLegend"
              class="d-flex justify-center"
              :symbol="legendItems?.[dataSetIndex].symbol"
              :color="legendItems?.[dataSetIndex].color"
              :enumeration="legendItems?.[dataSetIndex].seriesType === 'enumeration'"
            />

            <IdentifierLabel
              class="evaluationParameter"
              :subline="item[column.key].evaluationParameterType"
              :identifier="item[column.key].evaluationParameter.identifier"
              :label="item[column.key].evaluationParameter.label"
            />

            <div v-if="item[column.key].evaluationType" class="evaluationType">
              <VDivider color="primary-lighten-2" class="my-1" />
              {{ item[column.key].evaluationType }}
              {{
                item[column.key].evaluationUnitAbbreviation
                  ? `(${item[column.key].evaluationUnitAbbreviation})`
                  : ''
              }}

              <VDivider color="primary-lighten-2" class="my-1" />
            </div>

            <div v-if="item[column.key].definition" class="definition">
              {{ item[column.key].definition }}
              <VDivider color="primary-lighten-2" class="my-1" />
            </div>
            <div v-if="item[column.key].definitionParameterSetInfo" class="definitionParameterSet">
              <ShowMore
                color="rgb(var(--v-theme-primary-lighten-4))"
                :lessLabel="$t('HideDefinitionSetParameters')"
                :moreLabel="$t('ShowDefinitionSetParameters')"
                :collapsedHeight="75"
              >
                {{ item[column.key].definitionParameterSetInfo.displayValuesString }}
              </ShowMore>
            </div>
          </template>

          <!-- Type: Identifier Label -->
          <template v-if="item[column.key]?.type === 'identifier'">
            <IdentifierLabel
              :identifier="item[column.key].identifier"
              :label="item[column.key].label"
            />
          </template>

          <!-- Type: Support Systems -->
          <template v-if="item[column.key]?.type === 'supportSystems'">
            <div v-if="item[column.key].supportSystems.length" class="support-systems">
              <template
                v-for="(supportSystemGroup, index) in item[column.key].supportSystems"
                :key="supportSystemGroup.name || index"
              >
                <FormLabel
                  :label="
                    truthy(supportSystemGroup.name)
                      ? `${$t('Group_one')}: ${supportSystemGroup.name}`
                      : undefined
                  "
                >
                  <template
                    v-for="supportSystem in supportSystemGroup.items"
                    :key="supportSystem.id"
                  >
                    <v-chip>
                      <IdentifierLabel
                        :identifier="supportSystem.iuid"
                        :label="supportSystem.name"
                      />
                    </v-chip>
                  </template>
                </FormLabel>
              </template>
            </div>
            <template v-else>
              {{ EMPTY_FIELD_SIGN }}
            </template>
          </template>

          <!-- Type: Setup Parameter -->
          <template v-if="item[column.key]?.type === 'setupParameter'">
            <template v-if="item[column.key].parameter">
              <template v-if="instanceOfNumericDataSetSetupDto(item[column.key].parameter)">
                <FormLabel
                  v-for="value in item[column.key].parameter.values"
                  :key="`${item[column.key].parameter.id}-${value}`"
                  :class="
                    item[column.key].parameter.isIdenticalOverAllSetupValues
                      ? 'font-weight-bold'
                      : ''
                  "
                  :label="truthy(value.name) ? `${$t('Group_one')}: ${value.name}` : undefined"
                >
                  {{
                    toDisplayValue(value.value, {
                      fractionDigits: item[column.key].parameter.decimalPlaces,
                      unitAbbreviation: item[column.key].parameter.unitAbbreviation,
                      valueType: item[column.key].parameter.valueType, // ??
                    })
                  }}
                </FormLabel>
              </template>
              <template
                v-else-if="instanceOfEnumerationDataSetSetupDto(item[column.key].parameter)"
              >
                <FormLabel
                  v-for="value in item[column.key].parameter.values"
                  :key="`${item[column.key].parameter.id}-${value}`"
                  :class="
                    item[column.key].parameter.isIdenticalOverAllSetupValues
                      ? 'font-weight-bold'
                      : ''
                  "
                  :label="truthy(value.name) ? `${$t('Group_one')}: ${value.name}` : undefined"
                >
                  {{ getSetupParameterName(value.value) }}
                </FormLabel>
              </template>
              <template v-else-if="instanceOfTextDataSetSetupDto(item[column.key].parameter)">
                <FormLabel
                  v-for="value in item[column.key].parameter.values"
                  :key="`${item[column.key].parameter.id}-${value}`"
                  :label="truthy(value.name) ? `${$t('Group_one')}: ${value.name}` : undefined"
                  :class="
                    item[column.key].parameter.isIdenticalOverAllSetupValues
                      ? 'font-weight-bold'
                      : ''
                  "
                >
                  {{ value.value }}
                </FormLabel>
              </template>
            </template>
            <span v-else>{{ EMPTY_FIELD_SIGN }}</span>
          </template>

          <!-- Type: Text (default) -->
          <template v-if="item[column.key]?.type === 'text'">
            {{ item[column.key].value }}
          </template>
        </template>
      </template>
    </v-data-table>
  </div>
  <div v-else class="filter-empty-state d-flex align-center justify-center" v-bind="$attrs">
    <p class="mt-4">
      {{ filterResultsText }}
    </p>
  </div>
</template>

<style lang="scss" scoped>
@use '@/styles/vuetifySettings';
@use '@/styles/mixins';

:deep(.v-table__wrapper) {
  width: calc(100%);
}

$scroll-button-margin-from-table-edge: 22px;

.dataset-selection-wrapper {
  position: relative;
}

.scroll-button {
  z-index: 10;
  top: 0;
  position: absolute;
  background-color: white !important;
  border: none;
  height: 50px !important;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.scroll-button-left {
  left: -$scroll-button-margin-from-table-edge;
}

.scroll-button-right {
  right: -$scroll-button-margin-from-table-edge;
}

$hover-border-width: 3px;
$offset: 12px;

%column-hover {
  border-right: $hover-border-width solid rgba(var(--v-theme-primary-lighten-2));
  border-left: $hover-border-width solid rgba(var(--v-theme-primary-lighten-2));
}
%column-selected {
  border-right: $hover-border-width solid rgba(var(--v-theme-primary-darken-3));
  border-left: $hover-border-width solid rgba(var(--v-theme-primary-darken-3));
}

.trends-table {
  position: relative;
  overflow-y: visible !important;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -$offset;
    width: $offset;
    height: 100%;
    background-color: rgb(var(--v-theme-primary-lighten-5));
    z-index: 1;
  }
  @include mixins.shadow-left(5);
  &:after {
    left: unset;
    right: 0;
    height: calc(100% + 16px);
  }
  :deep(.v-table__wrapper) {
    width: calc(100% + $offset);
    overflow-x: auto;

    position: relative;
    left: -$offset;

    // Make sure table layout is fixed to maintain column widths during scrolling
    table {
      table-layout: fixed;
      border-collapse: separate;
      border-spacing: vuetifySettings.$spacer * 3 0;
      width: unset !important;
    }
    // Column hover and selection styles - performance optimized
    td.dataset-column.column-hovered {
      @extend %column-hover;
    }

    td.dataset-column.selected {
      @extend %column-selected;
    }

    // First and last row border styles for hovered columns
    tr:first-child > td.dataset-column.column-hovered {
      border-top: $hover-border-width solid rgba(var(--v-theme-primary-lighten-2));
    }

    tr:last-child > td.dataset-column.column-hovered {
      border-bottom: $hover-border-width solid rgba(var(--v-theme-primary-lighten-2));
    }

    // First and last row border styles for selected columns
    tr:first-child > td.dataset-column.selected {
      border-top: $hover-border-width solid rgba(var(--v-theme-primary-darken-3));
    }

    tr:last-child > td.dataset-column.selected {
      border-bottom: $hover-border-width solid rgba(var(--v-theme-primary-darken-3));
    }

    tr {
      &.separator-row {
        td {
          background-color: rgb(var(--v-theme-primary-lighten-3));
        }
      }
      &:not(:last-child) {
        td {
          border-bottom: 1px solid rgba(var(--v-theme-primary-lighten-4));

          &.dataset-column {
            border-bottom: none;
            box-shadow: inset 0 -1px 0 0 rgba(var(--v-theme-primary-lighten-4));
          }
        }
      }
      td {
        background-color: rgb(var(--v-theme-surface));
        padding: 0;
        border-block: none;

        white-space: pre-line;

        transition: border-color 0.3s ease-in-out;

        &.dataset-column {
          border-right: $hover-border-width solid rgba(var(--v-theme-primary-lighten-4));
          border-left: $hover-border-width solid rgba(var(--v-theme-primary-lighten-4));
          min-width: 300px;
          max-width: 300px;

          &.selected {
            --column-border-color: rgb(var(--v-theme-primary));
          }
        }

        &.header-column {
          position: sticky;
          padding: vuetifySettings.$spacer vuetifySettings.$spacer * 2;
          left: 12px;
          z-index: 2;
          background-color: rgb(var(--v-theme-primary-lighten-4));
          font-weight: bold;
          min-width: 330px;
          max-width: 330px;
          border-bottom: 1px solid rgba(var(--v-theme-primary-lighten-5));

          @include mixins.shadow-right(5);
        }

        &.dataset-cell {
          height: 100%;
          cursor: pointer;
          padding: vuetifySettings.$spacer vuetifySettings.$spacer * 2;

          .v-chip {
            margin-bottom: vuetifySettings.$spacer;
          }
        }
      }
      &:first-child {
        td.dataset-column {
          background-color: rgb(var(--v-theme-primary-lighten-4));
          padding: vuetifySettings.$spacer * 2;

          vertical-align: top;

          border-top-left-radius: vuetifySettings.$border-radius-root * 3 !important;
          border-top-right-radius: vuetifySettings.$border-radius-root * 3 !important;

          border-top: $hover-border-width solid rgba(var(--v-theme-primary-lighten-4));

          & .evaluationParameter {
            font-size: 1.01rem;
          }
        }
      }
      &:last-child {
        td.dataset-column {
          border-bottom-left-radius: vuetifySettings.$border-radius-root * 3 !important;
          border-bottom-right-radius: vuetifySettings.$border-radius-root * 3 !important;

          border-bottom: $hover-border-width solid rgba(var(--v-theme-primary-lighten-4));
        }
      }
    }
  }

  .header-cell {
    padding: vuetifySettings.$spacer * 3;
    height: 100%;
    display: flex;
    align-items: center;
  }
}

.filter-empty-state {
  height: calc(100vh - var(--search-area-top) - 10vh);
}
</style>
